import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Video, CalendarPlus, LogIn, Copy, Play, Calendar, Clock } from 'lucide-react';
import { useAuthContext } from '@/features/auth/AuthProvider';
import { useCreateInstant, useMyMeetings } from '@/features/meetings/useMeetings';
import { useEnterMeeting } from '@/features/meetings/useEnterMeeting';
import type { Meeting } from '@/features/meetings/meeting.api';

export default function DashboardPage(): JSX.Element {
  const { user } = useAuthContext();
  const createInstant = useCreateInstant();
  const enterMeeting = useEnterMeeting();
  const { data, isLoading } = useMyMeetings({ status: 'SCHEDULED' });

  const startInstant = async () => {
    const m = await createInstant.mutateAsync('Instant Meeting');
    await enterMeeting(m);
  };

  const upcoming = data?.data.meetings ?? [];

  return (
    <div className="space-y-8">
      {/* greeting banner */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-brand-600 via-indigo-600 to-fuchsia-600 p-8 text-white shadow-xl">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
        <h1 className="relative text-3xl font-black">Welcome back, {user?.firstName} 👋</h1>
        <p className="relative mt-1 text-indigo-100">Start or join a meeting in one click.</p>
        {!user?.emailVerified && (
          <div className="relative mt-4 inline-block rounded-lg bg-white/15 px-4 py-2 text-sm backdrop-blur">
            📧 Please verify your email to unlock all features.
          </div>
        )}
      </div>

      {/* action cards */}
      <div className="grid gap-5 sm:grid-cols-3">
        <ActionCard
          onClick={startInstant}
          loading={createInstant.isPending}
          icon={Video}
          grad="from-blue-500 to-indigo-600"
          title="New meeting"
          desc="Start an instant call"
        />
        <ActionCard to="/schedule" icon={CalendarPlus} grad="from-fuchsia-500 to-pink-600" title="Schedule" desc="Plan for later" />
        <ActionCard to="/join" icon={LogIn} grad="from-emerald-500 to-teal-600" title="Join" desc="Enter a code" />
      </div>

      {/* upcoming */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Upcoming meetings</h2>
          <Link to="/meetings" className="text-sm font-medium text-brand-600 hover:underline">View all →</Link>
        </div>

        {isLoading ? (
          <div className="card text-sm text-slate-500">Loading…</div>
        ) : upcoming.length === 0 ? (
          <div className="card flex flex-col items-center gap-3 py-10 text-center">
            <Calendar className="h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">No upcoming meetings yet.</p>
            <Link to="/schedule" className="btn-primary">Schedule one</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {upcoming.map((m) => (
              <UpcomingRow key={m.id} meeting={m} onJoin={() => enterMeeting(m)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionCard({
  to,
  onClick,
  loading,
  icon: Icon,
  grad,
  title,
  desc,
}: {
  to?: string;
  onClick?: () => void;
  loading?: boolean;
  icon: typeof Video;
  grad: string;
  title: string;
  desc: string;
}): JSX.Element {
  const inner = (
    <>
      <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${grad} text-white shadow-lg`}>
        <Icon size={22} />
      </div>
      <h3 className="text-lg font-bold">{loading ? 'Starting…' : title}</h3>
      <p className="text-sm text-slate-500">{desc}</p>
    </>
  );
  const cls =
    'rounded-2xl border border-slate-200/70 bg-white/80 p-6 text-left shadow-sm backdrop-blur transition hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900/70';
  return to ? (
    <Link to={to} className={cls}>{inner}</Link>
  ) : (
    <button onClick={onClick} disabled={loading} className={`${cls} disabled:opacity-60`}>{inner}</button>
  );
}

function UpcomingRow({ meeting: m, onJoin }: { meeting: Meeting; onJoin: () => void }): JSX.Element {
  const copy = () => {
    navigator.clipboard.writeText(m.inviteUrl);
    toast.success('Invite link copied');
  };
  return (
    <div className="flex items-center justify-between rounded-2xl border border-slate-200/70 bg-white/80 p-4 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/70">
      <div className="flex items-center gap-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-indigo-600 text-white">
          <Video size={20} />
        </div>
        <div>
          <h3 className="font-semibold">{m.title}</h3>
          <p className="flex items-center gap-1.5 text-sm text-slate-500">
            <Clock size={13} />
            {m.scheduledStart ? new Date(m.scheduledStart).toLocaleString() : 'No time set'} · {m.meetingCode}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={copy} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" title="Copy invite">
          <Copy size={16} />
        </button>
        <button onClick={onJoin} className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:shadow-lg">
          <Play size={14} /> Join
        </button>
      </div>
    </div>
  );
}
